import { useSyncExternalStore } from "react";
import { draftSchema, EMPTY_DRAFT, type Draft } from "./draft";

export const DRAFT_STORAGE_KEY = "antar.onboarding.draft";

/** The subset of Storage the store uses; injectable for tests. */
export type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/**
 * Where the draft lives between screens — and across the Google redirect.
 *
 * Signing in with Google leaves the page and comes back to it, so anything
 * held only in React state would be lost at exactly the moment the person
 * has finished answering. The draft is therefore mirrored to storage on every
 * change and read back on start. Storage can be absent or throw (private
 * windows, cleared site data); every access is guarded and the store keeps
 * working in memory.
 */
export interface DraftStore {
  getSnapshot(): Draft;
  subscribe(listener: () => void): () => void;
  update(updater: (draft: Draft) => Draft): void;
  clear(): void;
}

export function createDraftStore(storage: StorageLike | null): DraftStore {
  let draft = read(storage);
  const listeners = new Set<() => void>();

  function publish(next: Draft): void {
    draft = next;
    write(storage, next);
    for (const listener of listeners) listener();
  }

  return {
    getSnapshot: () => draft,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    update(updater) {
      publish(updater(draft));
    },
    clear() {
      draft = EMPTY_DRAFT;
      try {
        storage?.removeItem(DRAFT_STORAGE_KEY);
      } catch {
        // Nothing to remove, or nowhere to remove it from.
      }
      for (const listener of listeners) listener();
    },
  };
}

function read(storage: StorageLike | null): Draft {
  try {
    const raw = storage?.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return EMPTY_DRAFT;
    const parsed = draftSchema.safeParse(JSON.parse(raw));
    return parsed.success ? Object.freeze(parsed.data) : EMPTY_DRAFT;
  } catch {
    return EMPTY_DRAFT;
  }
}

function write(storage: StorageLike | null, draft: Draft): void {
  try {
    storage?.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Quota or a private window: the in-memory draft still works.
  }
}

/** The browser's localStorage, or null where it is blocked. */
export function browserStorage(): StorageLike | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export function useDraft(store: DraftStore): Draft {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
